import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import './IntegratedSearchBar.css';

const IntegratedSearchBar = ({ onSearch, onCategoryChange, selectedCategory, loading, currentSort, onSortChange, activeFilters = [], onFilterChange, hideFilters = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    setError(null);
    try {
      const response = await axios.get('/api/deals/categories');
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSearch(searchTerm);
  };

  const handleClear = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    onCategoryChange(value);
  };

  const toggleFilter = (filterName) => {
    if (!onFilterChange) return;
    const newFilters = activeFilters.includes(filterName)
      ? activeFilters.filter(f => f !== filterName)
      : [...activeFilters, filterName];
    onFilterChange(newFilters);
  };

  return (
    <div className="integrated-search-container mb-4">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8">
            <form onSubmit={handleSubmit} className="integrated-search-form">
              <div className="search-input-group">
                {/* Category Dropdown */}
                <div className="category-selector">
                  <select
                    className="form-select category-dropdown"
                    value={selectedCategory || 'all'}
                    onChange={handleCategoryChange}
                    disabled={loading || loadingCategories}
                    aria-label="Filter by category"
                  >
                    <option value="all">All Categories</option>
                    {loadingCategories ? (
                      <option disabled>Loading...</option>
                    ) : error ? (
                      <option disabled>Error loading</option>
                    ) : (
                      categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="dropdown-arrow">
                    <i className="fas fa-chevron-down"></i>
                  </div>
                </div>

                {/* Search Input */}
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    className="form-control search-input"
                    placeholder="Search for deals..."
                    value={searchTerm}
                    onChange={handleInputChange}
                    disabled={loading}
                    aria-label="Search deals"
                  />

                  {/* Action Buttons */}
                  <div className="search-actions">
                    {searchTerm && (
                      <button
                        type="button"
                        className="btn btn-clear"
                        onClick={handleClear}
                        disabled={loading}
                        title="Clear search"
                        aria-label="Clear search"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <button
                      type="submit"
                      className="btn btn-search"
                      disabled={loading}
                      aria-label="Search"
                    >
                      {loading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-search"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sort + Filter Row */}
              {!hideFilters && (
                <div className="sort-filter-row">
                  <div className="filter-pills">
                    <button
                      type="button"
                      className={`filter-pill ${activeFilters.includes('lowest_price') ? 'filter-pill--active' : ''}`}
                      onClick={() => toggleFilter('lowest_price')}
                      disabled={loading}
                    >
                      🏷️ Lowest Price
                    </button>
                    <button
                      type="button"
                      className={`filter-pill ${activeFilters.includes('best_seller') ? 'filter-pill--active' : ''}`}
                      onClick={() => toggleFilter('best_seller')}
                      disabled={loading}
                    >
                      🏆 Best Seller
                    </button>
                  </div>
                  <select
                    className="sort-select"
                    value={currentSort || 'relevance'}
                    onChange={(e) => onSortChange && onSortChange(e.target.value)}
                    disabled={loading}
                    aria-label="Sort deals"
                  >
                    <option value="relevance">Sort: Relevance</option>
                    <option value="recency">Sort: Newest</option>
                    <option value="popularity">Sort: Most Popular</option>
                  </select>
                </div>
              )}

              {/* Status Messages */}
              {error && (
                <div className="status-message error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{error}</span>
                  <button
                    type="button"
                    className="btn btn-link retry-btn"
                    onClick={fetchCategories}
                    disabled={loadingCategories}
                  >
                    Retry
                  </button>
                </div>
              )}

              {loadingCategories && (
                <div className="status-message loading-message">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading categories...</span>
                </div>
              )}

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

IntegratedSearchBar.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  selectedCategory: PropTypes.string,
  loading: PropTypes.bool,
  currentSort: PropTypes.string,
  onSortChange: PropTypes.func,
  activeFilters: PropTypes.arrayOf(PropTypes.string),
  onFilterChange: PropTypes.func,
  hideFilters: PropTypes.bool,
};

IntegratedSearchBar.defaultProps = {
  selectedCategory: 'all',
  loading: false,
  currentSort: 'relevance',
  onSortChange: null,
  activeFilters: [],
  onFilterChange: null,
};

export default IntegratedSearchBar;
